/**
 * SKÄRMKARTAN — appens enda register över vilka skärmar som finns.
 *
 * NAMNEN ÄR PROTOTYPENS. data-screen i design/appboken.html heter ordagrant
 * samma sak som `id` nedan, och `titel` och `foralder` är avskrivna ur
 * prototypens egna `titles`- och `foralder`-tabeller (rad 1236-1244). Den som
 * granskar visuellt ska kunna lägga filerna sida vid sida.
 *
 * TVÅ LÄGEN, OCH `lage` ÄR SPÄRREN. Beslut: 'hem' (Mitt jobb) ser alla,
 * 'lag' (Laget) ser bara den som har full vy. Fältet är inte en etikett utan
 * det som avgör — `synligaSkarmar()` filtrerar bort hela raden, så en montör
 * kan inte nå 'attest' ens genom att skriva adressen. Se app/lage.tsx.
 *
 * SÅ HÄR LÄGGER DU TILL EN SKÄRM (det är hela registreringen):
 *   1. En rad i SKARMAR nedan: id, sokvag, titel, foralder, lage.
 *   2. En rad i src/app/vyer.tsx som binder id:t till din komponent.
 * Inget mer. Ingen router att ändra, ingen meny att uppdatera, ingen
 * bakåtknapp att koppla — foralder-kedjan ger den.
 *
 * SÖKVÄGARNA ÄR SVENSKA och matchas segmentvis, precis som workern gör i
 * router/route-table.ts: ett segment som börjar på ':' är en parameter. Ett
 * reguljärt uttryck byggt av strängar hade brutit på första id:t med ett
 * bindestreck.
 */

export type Lage = 'hem' | 'lag';

export type SkarmId =
  | 'hem'
  | 'lag'
  | 'ordrar'
  | 'ordny'
  | 'order'
  | 'tidny'
  | 'tid'
  | 'kalender'
  | 'mil'
  | 'utlagg'
  | 'installningar'
  | 'profil'
  | 'nyfirma'
  | 'igang'
  | 'attest'
  | 'var'
  | 'inhyrda'
  | 'fakturera'
  | 'moment'
  | 'kunder'
  | 'timpriser'
  | 'personal'
  | 'prenumeration'
  | 'raderafirma';

export interface Skarm {
  readonly id: SkarmId;
  /** Segmentvis mönster. ':namn' är en parameter. */
  readonly sokvag: string;
  /** Texten i toppraden. 'Klartid' på de två startsidorna. */
  readonly titel: string;
  /**
   * Skärmen bakåtknappen går till. null = startsida, och då visas ingen
   * bakåtknapp alls.
   */
  readonly foralder: SkarmId | null;
  /** Vilket läge skärmen hör till. Avgör både menyn och åtkomsten. */
  readonly lage: Lage;
}

export const SKARMAR = [
  /* --- Mitt jobb: alla ser det här -------------------------------------- */
  { id: 'hem', sokvag: '/', titel: 'Klartid', foralder: null, lage: 'hem' },
  { id: 'ordrar', sokvag: '/ordrar', titel: 'Arbetsordrar', foralder: 'hem', lage: 'hem' },
  // RADORDNINGEN ÄR INTE FRI: '/ordrar/ny' matchar även mönstret
  // '/ordrar/:work_order_id', och `traffa()` tar FÖRSTA träffen bland de synliga
  // skärmarna. Raden MÅSTE därför stå före 'order'. Flyttas den nedanför blir
  // "Ny arbetsorder" en arbetsorder med id 'ny' som aldrig finns.
  // lage 'lag' för att 'put:work_order' är MANAGER_ONLY i SYNC_RULES — en montör
  // ska inte ha adressen i sin karta alls. Bokföraren har full vy och NÅR
  // skärmen, men möts av ett besked i stället för fält (beslut 07).
  { id: 'ordny', sokvag: '/ordrar/ny', titel: 'Ny arbetsorder', foralder: 'ordrar', lage: 'lag' },
  { id: 'order', sokvag: '/ordrar/:work_order_id', titel: 'Arbetsorder', foralder: 'ordrar', lage: 'hem' },
  { id: 'tid', sokvag: '/tid', titel: 'Tidrapport', foralder: 'hem', lage: 'hem' },
  { id: 'tidny', sokvag: '/tid/ny', titel: 'Ny tidrapport', foralder: 'order', lage: 'hem' },
  // :dag är arbetsdagen man tryckte på i startsidans lilla rutnät, så att den
  // stora kalendern öppnas PÅ den dagen och inte på dagens datum.
  { id: 'kalender', sokvag: '/kalender/:dag', titel: 'Kalender', foralder: 'hem', lage: 'hem' },
  { id: 'mil', sokvag: '/mil', titel: 'Mil', foralder: 'hem', lage: 'hem' },
  { id: 'utlagg', sokvag: '/utlagg', titel: 'Utlägg', foralder: 'hem', lage: 'hem' },
  // Kugghjulet i toppraden. lage 'hem' för att ALLA måste kunna nå sina
  // inställningar — också en montör, som inte har lagets skärmar i sin karta.
  { id: 'installningar', sokvag: '/installningar', titel: 'Inställningar', foralder: 'hem', lage: 'hem' },
  { id: 'profil', sokvag: '/installningar/profil', titel: 'Mitt konto', foralder: 'installningar', lage: 'hem' },
  // lage 'hem' för att CREATE_COMPANY har auth 'account' och NO_ROLES: att lägga
  // upp en egen firma hör till kontot och inte till någon roll i en firma man
  // redan är med i. En inhyrd (beslut 17) är gäst hos någon annan och driver
  // eget vid sidan av — han ska nå skärmen, och han har inte lagets läge.
  // Samma skärm renderas dessutom i världen 'ingen_firma' (App.tsx), som ligger
  // utanför navigeringen; se NyFirma.tsx.
  { id: 'nyfirma', sokvag: '/ny-firma', titel: 'Ny firma', foralder: 'installningar', lage: 'hem' },
  // KOM IGÅNG — de tre frågorna en ny firma måste svara på innan en timme går
  // att rapportera. lage 'lag' för att varje steg skriver på en MANAGER_ONLY-rutt
  // ('put:price_rate', 'put:task', 'put:customer', 'put:work_order'): en montör
  // ska inte ha adressen i sin karta, och kan inte åtgärda något ändå. Föräldern
  // är 'hem' och inte 'installningar' — skärmen nås från startsidans kort, och
  // bakåtknappen ska gå tillbaka dit man kom ifrån. Bokföraren har full vy och
  // NÅR skärmen, men möts av ett besked i stället för fält (beslut 07).
  { id: 'igang', sokvag: '/kom-igang', titel: 'Kom igång', foralder: 'hem', lage: 'lag' },

  /* --- Laget: bara full vy (manager, bookkeeper) ------------------------ */
  { id: 'lag', sokvag: '/laget', titel: 'Klartid', foralder: null, lage: 'lag' },
  { id: 'attest', sokvag: '/attestera', titel: 'Attestera', foralder: 'lag', lage: 'lag' },
  { id: 'var', sokvag: '/laget/idag', titel: 'Laget idag', foralder: 'lag', lage: 'lag' },
  { id: 'inhyrda', sokvag: '/laget/inhyrda', titel: 'Inhyrda', foralder: 'lag', lage: 'lag' },
  { id: 'fakturera', sokvag: '/laget/fakturera', titel: 'Att fakturera', foralder: 'lag', lage: 'lag' },

  /* --- Firmans stamdata: nås bakom kugghjulet, men hör till lagets läge ---
     De fyra ligger under 'installningar' i FÖRÄLDERKEDJAN — det är där chefen
     letar efter dem — men de har lage 'lag', för alla fyra skriver på
     MANAGER_ONLY-rutter ('put:task', 'put:price_rate', 'put:company_settings',
     medlemsrutterna, BILLING_CHECKOUT). En montör ska inte ha adresserna i sin
     karta alls. Bokföraren har full vy och NÅR dem, och varje skärm visar då ett
     besked i stället för fält, precis som NyOrder.tsx (beslut 07).

     '/prenumeration' MÅSTE stå ordagrant så: Stripe skickar tillbaka kunden dit
     efter betalningen (worker/src/modules/billing/subscription.ts rad 126-127
     och 148). En annan adress här och kunden landar på startsidan utan besked. */
  { id: 'moment', sokvag: '/installningar/moment', titel: 'Moment', foralder: 'installningar', lage: 'lag' },
  // ROT-SATSEN BOR PÅ KUNDEN. Utan den här skärmen går den bara att sätta i
  // samma andetag som kunden skapas (NyOrder.tsx), och en kund som redan ligger
  // i registret är permanent utan ROT — se Kunder.tsx.
  { id: 'kunder', sokvag: '/installningar/kunder', titel: 'Kunder', foralder: 'installningar', lage: 'lag' },
  { id: 'timpriser', sokvag: '/installningar/timpriser', titel: 'Timpriser', foralder: 'installningar', lage: 'lag' },
  { id: 'personal', sokvag: '/installningar/personal', titel: 'Personal', foralder: 'installningar', lage: 'lag' },
  { id: 'prenumeration', sokvag: '/prenumeration', titel: 'Prenumeration', foralder: 'installningar', lage: 'lag' },
  // RADERINGEN (villkoren punkt 10.7). En egen skärm och inte en dialogruta:
  // bekräftelsen är firmans org.nr, och den som skriver det ska ha namn,
  // nummer, följderna och vägen till exporten framför sig — med en riktig
  // bakåtknapp till Inställningar. lage 'lag' för att DELETE_COMPANY är
  // MANAGER_ONLY; att den dessutom kräver ÄGAREN (requires_owner) prövas i
  // skärmen, som visar ett besked i stället för fältet åt alla andra. Raden i
  // Inställningar som leder hit renderas över huvud taget inte för dem.
  { id: 'raderafirma', sokvag: '/installningar/radera-firman', titel: 'Radera firman', foralder: 'installningar', lage: 'lag' },
] as const satisfies readonly Skarm[];

/** Startsidan i respektive läge. */
export const STARTSKARM = { hem: 'hem', lag: 'lag' } as const satisfies Record<Lage, SkarmId>;

export const skarmMedId = (id: SkarmId): Skarm => {
  const traff = SKARMAR.find((s) => s.id === id);
  // SKARMAR är `satisfies readonly Skarm[]` över samma union som SkarmId, så
  // det här kan bara hända om någon lagt till ett id utan sin rad.
  if (traff === undefined) throw new Error(`skärmen ${id} saknar rad i SKARMAR`);
  return traff;
};

/**
 * Skärmarna en användare med det här läget får se. Används både av routern och
 * av menyn, så att de aldrig kan säga olika saker.
 */
export const synligaSkarmar = (kanSeLaget: boolean): readonly Skarm[] =>
  kanSeLaget ? SKARMAR : SKARMAR.filter((s) => s.lage === 'hem');

/** Kedjan upp till startsidan: ['tidny', 'order', 'ordrar', 'hem']. */
export const foralderkedja = (id: SkarmId): readonly SkarmId[] => {
  const kedja: SkarmId[] = [id];
  let nuvarande = skarmMedId(id).foralder;
  while (nuvarande !== null) {
    kedja.push(nuvarande);
    nuvarande = skarmMedId(nuvarande).foralder;
  }
  return kedja;
};
